import type {
  RawComponent,
  RawContentUnit,
} from '@/features/content/useComponent';
import { getKaiCoachSection } from '@/features/kaiCoach/kaiCoachSection';

export interface TitleGroup {
  contentUnitIds: string[];
  selected: boolean;
  title: string;
}

export interface GenerateCourseComponentSelection {
  allContentUnitIds?: string[];
  isSectionDivider?: boolean;
  selected: boolean;
  titleGroups?: TitleGroup[];
  uid: string;
}

export interface GenerateCourseComponentParam {
  content_unit_ids?: string[];
  include_all_questions?: boolean;
  include_video?: boolean;
  uid: string;
}

export interface GenerateCourseRequest {
  component_params: GenerateCourseComponentParam[];
  course_name: string;
  include_all_question_settings: boolean;
  instructor_id: string;
  seed_course_id: string;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

/**
 * Addresses a content unit by where it sits in the component.
 *
 * This used to be `componentUid + heading`. A heading is not a usable
 * identifier: untitled units have none, so they could not be selected and a
 * section that opened with one had that opening silently dropped from the copy.
 * Headings are also not unique within a component, so two sections with the
 * same title collapsed onto one id.
 *
 * These ids never reach the datastore — they are built here, sent in a single
 * GenerateCourse request, and matched against the seed component server-side.
 * Must stay in step with contentUnitPositionID in
 * backend/app/generate-course-api.go.
 */
export function buildContentUnitId(
  componentUid: string,
  sectionIndex: number,
  unitIndex: number,
) {
  return `${componentUid}#${sectionIndex}:${unitIndex}`;
}

export function isTocVisibleContentUnit(unit: RawContentUnit) {
  const heading = normalizeString(unit.heading);
  const collapseByDefault = normalizeBoolean(
    unit.collapse_by_default ?? unit.collapseByDefault,
  );
  const disableAutoNumbering = normalizeBoolean(
    unit.disable_auto_numbering ?? unit.disableAutoNumbering,
  );

  return Boolean(
    heading && collapseByDefault !== true && disableAutoNumbering !== true,
  );
}

/** The label given to a run of untitled units that opens a component. */
export const INTRO_GROUP_TITLE = 'Introduction';

interface PlacedContentUnit {
  id: string;
  unit: RawContentUnit;
}

/**
 * Walks a component's content units in document order, pairing each with the
 * positional id the backend will match it against. Sections without a content
 * section still consume an index, because the backend counts sections the same
 * way — skipping them here would shift every id after the gap.
 */
function listPlacedContentUnits(component: RawComponent): PlacedContentUnit[] {
  const componentUid = normalizeString(component.uid);

  if (!componentUid) {
    return [];
  }

  return (component.sections ?? []).flatMap((section, sectionIndex) => {
    const contentSection = section.content_section ?? section.contentSection ?? null;
    const contentUnits =
      contentSection?.content_units ?? contentSection?.contentUnits ?? [];

    if (!Array.isArray(contentUnits)) {
      return [];
    }

    return contentUnits.map((unit, unitIndex) => ({
      id: buildContentUnitId(componentUid, sectionIndex, unitIndex),
      unit: unit as RawContentUnit,
    }));
  });
}

/**
 * Groups a component's content units under the heading that introduces them.
 *
 * Units before the first heading are their own group rather than being dropped:
 * a chapter that opens with an untitled intro paragraph or image used to lose
 * that opening entirely, because an unheaded unit had no id to be selected by.
 */
export function buildTitleGroups(component: RawComponent): TitleGroup[] {
  const groups: TitleGroup[] = [];
  let currentGroup: TitleGroup | null = null;

  listPlacedContentUnits(component).forEach(({ id, unit }) => {
    const heading = normalizeString(unit.heading);

    if (heading && isTocVisibleContentUnit(unit)) {
      currentGroup = {
        contentUnitIds: [id],
        selected: true,
        title: heading,
      };
      groups.push(currentGroup);
      return;
    }

    if (!currentGroup) {
      currentGroup = {
        contentUnitIds: [],
        selected: true,
        title: INTRO_GROUP_TITLE,
      };
      groups.push(currentGroup);
    }

    currentGroup.contentUnitIds.push(id);
  });

  return groups;
}

/**
 * Every content unit in the component, untitled ones included. An id is emitted
 * per unit, so "copy the whole chapter" copies the whole chapter.
 */
export function buildAllContentUnitIds(component: RawComponent) {
  return [...new Set(listPlacedContentUnits(component).map(({ id }) => id))];
}

export function getSelectedContentUnitIds(
  component: GenerateCourseComponentSelection,
) {
  if (!component.selected) {
    return [];
  }

  if (component.allContentUnitIds && component.allContentUnitIds.length > 0) {
    const allGroupsSelected = (component.titleGroups ?? []).every(
      (group) => group.selected,
    );

    if (component.titleGroups === undefined || allGroupsSelected) {
      return [...new Set(component.allContentUnitIds)];
    }
  }

  return [
    ...new Set(
      (component.titleGroups ?? [])
        .filter((group) => group.selected)
        .flatMap((group) => group.contentUnitIds),
    ),
  ];
}

export function buildGenerateCoursePayload({
  components,
  courseName,
  includeSettings,
  instructorId,
  seedCourseId,
}: {
  components: GenerateCourseComponentSelection[];
  courseName: string;
  includeSettings: boolean;
  instructorId: string;
  seedCourseId: string;
}): GenerateCourseRequest {
  return {
    component_params: components
      .filter((component) => component.selected)
      .map((component) => {
        if (component.isSectionDivider) {
          return {
            uid: component.uid,
          } satisfies GenerateCourseComponentParam;
        }

        return {
          content_unit_ids: getSelectedContentUnitIds(component),
          include_all_questions: true,
          include_video: true,
          uid: component.uid,
        } satisfies GenerateCourseComponentParam;
      }),
    course_name: courseName.trim(),
    include_all_question_settings: includeSettings,
    instructor_id: instructorId.trim(),
    seed_course_id: seedCourseId.trim(),
  };
}

/**
 * The chapters a Kai Coach module's sessions and activities read as background,
 * in order and once each. A copy of the module reads only those copied with it.
 */
export function getCoachContextChapterUids(component: RawComponent) {
  const sessions = getKaiCoachSection(component)?.sessions ?? [];

  return [
    ...new Set(
      sessions.flatMap((session) => [
        ...(session.context_component_uids ?? session.contextComponentUids ?? []),
        ...(session.activities ?? []).flatMap(
          (activity) =>
            activity.context_component_uids ?? activity.contextComponentUids ?? [],
        ),
      ]),
    ),
  ];
}
